
import React, { useState, useEffect, useCallback } from 'react';
import { WhatsAppButton } from './WhatsAppButton';
import { MenuIcon } from './icons/MenuIcon';
import { CloseIcon } from './icons/CloseIcon';
import { useTranslation } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { customSmoothScroll, getScrollTargetPosition } from '../utils/scroll';

interface NavLinkProps {
  href: string;
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  ariaLabel: string;
}

const NavLink: React.FC<NavLinkProps> = ({ href, children, onClick, ariaLabel }) => (
  <a
    href={href}
    onClick={onClick}
    className="text-brand-text hover:text-brand-accent px-3 py-2 rounded-md text-sm font-medium transition-colors"
    aria-label={ariaLabel}
  >
    {children}
  </a>
);

export const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => { setIsScrolled(window.scrollY > 20); };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = t('navbar.links') as { href: string; text: string }[];

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const handleSmoothScroll = useCallback((event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();
    const targetId = href.startsWith('#') ? href.substring(1) : null;
    if (!targetId) return;

    const targetY = getScrollTargetPosition(targetId);
    customSmoothScroll(targetY, 2000); // Sincronizado para 2 segundos

    try { if (window.history.pushState) window.history.pushState(null, "", `#${targetId}`); } catch (e) {}
    closeMenu();
  }, [closeMenu]);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ease-in-out animate-on-load animate-slideInDown ${isScrolled || isOpen ? 'bg-brand-secondary/70 backdrop-blur-md shadow-lg' : 'bg-transparent'}`} aria-label={t('navbar.ariaLabel')} style={{ animationFillMode: 'forwards' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <a href="#inicio" onClick={(e) => handleSmoothScroll(e, '#inicio')} className="text-2xl font-serif font-bold text-brand-light hover:text-brand-accent transition-colors">
              {t('navbar.brand')}
            </a>
          </div>
          <div className="hidden md:flex items-center gap-4">
              <div className="flex items-baseline space-x-1">
                {navLinks.map(link => (
                  <NavLink key={link.href} href={link.href} onClick={(e) => handleSmoothScroll(e, link.href)} ariaLabel={`${t('navbar.ariaNavigate')} ${link.text}`}>
                    {link.text}
                  </NavLink>
                ))}
              </div>
              <WhatsAppButton text={t('navbar.cta')} className="text-sm py-2 px-4 ml-2 text-white border-none shadow-md hover:shadow-lg" iconSize="w-4 h-4" />
              <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            <button onClick={toggleMenu} type="button" className="bg-brand-secondary/80 inline-flex items-center justify-center p-2 rounded-md text-brand-text hover:text-brand-light hover:bg-brand-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-secondary focus:ring-brand-light">
              <span className="sr-only">{t('navbar.mobileMenuButton')}</span>
              {isOpen ? <CloseIcon className="block h-6 w-6" /> : <MenuIcon className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden bg-brand-secondary/95 backdrop-blur-md border-t border-slate-700" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map(link => 
              <a key={link.href} href={link.href} onClick={(e) => handleSmoothScroll(e, link.href)} className="block text-brand-text hover:text-brand-accent hover:bg-brand-primary/50 px-3 py-2 rounded-md text-base font-medium transition-colors" aria-label={`${t('navbar.ariaNavigate')} ${link.text}`}>
                {link.text}
              </a>
            )}
          </div>
          <div className="pt-4 pb-3 border-t border-slate-700 px-5">
             <WhatsAppButton text={t('navbar.mobileCta')} className="w-full text-sm text-white border-none shadow-md hover:shadow-lg" />
          </div>
        </div>
      )}
    </nav>
  );
};
